import React, { useState, useEffect } from 'react';
import { Input, Select, DateInput } from '@/components/common/FormField';
import { 
  TransactionQueryParams, 
  TIMEZONE_OPTIONS,
  TransactionType,
  SourceSystem 
} from '@/app/types/transactions';
import { 
  getTransactionTypeOptions, 
  getSourceSystemOptions
} from '@/app/types/transactions';
import { partnerService } from '@/app/services/partnerService';

import './TransactionFilters.scss';

interface TransactionFiltersProps {
  filters: TransactionQueryParams;
  onFiltersChange: (filters: TransactionQueryParams) => void;
  onSearch: () => void;
  onClear: () => void;
}

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
}) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed on mobile
  const [partnerOptions, setPartnerOptions] = useState<{ value: string; label: string }[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [showOnlyActivePartners, setShowOnlyActivePartners] = useState(true);

  // Fetch partners for dropdown
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setPartnersLoading(true);
        // Fetch partners based on active filter toggle
        const response = await partnerService.queryPartners({
          is_active: showOnlyActivePartners ? true : undefined, // Show only active or all based on toggle
          limit: 1000, // Get all partners
          offset: 0,
        });
        
        // Sort partners by DBA (doing_business_as) alphabetically
        const sortedPartners = [...response.partners].sort((a, b) => {
          const dbaA = (a.doing_business_as || '').toLowerCase();
          const dbaB = (b.doing_business_as || '').toLowerCase();
          return dbaA.localeCompare(dbaB);
        });
        
        const options = [
          { value: '', label: 'All Partners' },
          ...sortedPartners.map((p) => ({
            value: p.partner_product_id.toString(),
            label: `${p.doing_business_as} (${p.partner_product_id})`,
          })),
        ];
        setPartnerOptions(options);
      } catch (error) {
        console.error('Failed to fetch partners:', error);
        // Fallback to empty list on error
        setPartnerOptions([{ value: '', label: 'All Partners' }]);
      } finally {
        setPartnersLoading(false);
      }
    };

    fetchPartners();
  }, [showOnlyActivePartners]);

  const handleChange = <K extends keyof TransactionQueryParams>(
    key: K,
    value: TransactionQueryParams[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    filters.community_ref_user_id ||
    filters.from_date ||
    filters.to_date ||
    filters.transaction_type ||
    filters.eligible_participant_id ||
    filters.transaction_id ||
    filters.exploration_signup_id ||
    filters.partner_product_id ||
    filters.source_system
  );

  const typeOptions = getTransactionTypeOptions();
  const sourceOptions = getSourceSystemOptions();

  return (
    <div className="transaction-filters" onKeyPress={handleKeyPress}>
      {/* Mobile Toggle Header */}
      <button 
        className="filters-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-expanded={!isCollapsed}
      >
        <span className="toggle-text">
          🔍 Filters
          {hasActiveFilters && <span className="active-indicator">●</span>}
        </span>
        <span className={`toggle-chevron ${isCollapsed ? '' : 'expanded'}`}>▼</span>
      </button>

      {/* Filter Content - Collapsible on Mobile */}
      <div className={`filters-content ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Primary Filters */}
        <div className="filters-primary">
          <Input
            label="User ID"
            value={filters.community_ref_user_id?.toString() || ''}
            onChange={(val) => handleChange('community_ref_user_id', val ? parseInt(val, 10) : undefined)}
            placeholder="Enter user ID"
            type="number"
          />

          <div className="date-range-group">
            <DateInput
              label="From Date"
              value={filters.from_date || ''}
              onChange={(val) => handleChange('from_date', val || undefined)}
            />
            <DateInput
              label="To Date"
              value={filters.to_date || ''}
              onChange={(val) => handleChange('to_date', val || undefined)}
            />
            <Select
              label="Timezone"
              value={filters.time_offset?.toString() || '0'}
              onChange={(val) => handleChange('time_offset', parseFloat(val))}
              options={TIMEZONE_OPTIONS.map(tz => ({
                value: tz.value.toString(),
                label: tz.abbreviation,
              }))}
            />
          </div>

          <Select
            label="Type"
            value={filters.transaction_type || ''}
            onChange={(val) => handleChange('transaction_type', (val as TransactionType) || undefined)}
            options={typeOptions.map(opt => ({
              value: opt.value,
              label: opt.label,
            }))}
          />
        </div>

        {/* More Filters Toggle */}
        <div className="more-filters-toggle">
          <button 
            className="btn-more" 
            onClick={() => setShowMoreFilters(!showMoreFilters)}
          >
            {showMoreFilters ? 'Less Filters' : 'More Filters'}
            <span className={`chevron ${showMoreFilters ? 'up' : 'down'}`}>▼</span>
          </button>
        </div>

        {/* Secondary Filters - Expandable */}
        {showMoreFilters && (
          <div className="filters-secondary">
            <Input
              label="Participant ID"
              value={filters.eligible_participant_id?.toString() || ''}
              onChange={(val) => handleChange('eligible_participant_id', val ? parseInt(val, 10) : undefined)}
              placeholder="Enter participant ID"
              type="number"
            />

            <Input
              label="Transaction ID"
              value={filters.transaction_id || ''}
              onChange={(val) => handleChange('transaction_id', val || undefined)}
              placeholder="Enter UUID"
            />

            <Input
              label="Exploration Signup ID"
              value={filters.exploration_signup_id?.toString() || ''}
              onChange={(val) => handleChange('exploration_signup_id', val ? parseInt(val, 10) : undefined)}
              placeholder="Enter signup ID"
              type="number"
            />

            <div className="partner-filter-group">
              <Select
                label="Partner"
                value={filters.partner_product_id?.toString() || ''}
                onChange={(val) => handleChange('partner_product_id', val ? parseInt(val, 10) : undefined)}
                options={partnerOptions}
                disabled={partnersLoading}
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showOnlyActivePartners}
                  onChange={(e) => setShowOnlyActivePartners(e.target.checked)}
                  disabled={partnersLoading}
                />
                <span>Show only active partners</span>
              </label>
            </div>

            <Select
              label="Source"
              value={filters.source_system || ''}
              onChange={(val) => handleChange('source_system', (val as SourceSystem) || undefined)}
              options={sourceOptions.map(opt => ({
                value: opt.value,
                label: opt.label,
              }))}
            />
          </div>
        )}

        {/* Search/Clear actions - always at bottom */}
        <div className="filter-actions">
          <button className="btn-primary" onClick={onSearch}>
            Search
          </button>
          <button className="btn-secondary" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionFilters;

